/**
 * Hook that returns the company-scoped Eden Treaty API client.
 *
 * Maps to /api/companies/:companyId/* routes on the backend.
 * Throws if the user has no active organization.
 */

import { useMemo } from "react"
import { api } from "../lib/api/client.ts"
import { useAuth } from "./useAuth.ts"

type CompanyApi = ReturnType<typeof api.api.companies>

export type { CompanyApi }

// Expose orgId globally for raw fetch fallbacks (audio uploads)
declare global {
  interface Window {
    __activeOrgId?: string
  }
}

export function useCompanyApi(): CompanyApi | null {
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null

  return useMemo(() => {
    if (!orgId) return null
    // Store globally for raw fetch fallbacks (Eden Treaty can't handle t.File() uploads)
    window.__activeOrgId = orgId
    // biome-ignore lint/suspicious/noExplicitAny: Eden Treaty dynamic path segments
    return (api.api.companies as any)[orgId] // eslint-disable-line @typescript-eslint/no-explicit-any
  }, [orgId])
}
